import { useEffect, useState } from 'react';
import { setSession } from './utils';
import axios from 'axios';
import { endpoints } from '@/lib/axios/axios-global';
import { useAppDispatch, useAppSelector } from '@/core/global-store';
import { setCredentials, getStoredMe } from '@/core/global-store/features/auth/authSlice';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@repo/ui/components/Loading/LoadingSpinner';
import { toast } from 'sonner';

const STORAGE_KEY = 'accessToken';

type Props = { children: React.ReactNode };

export function AuthInitializer({ children }: Props) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const existingMe = useAppSelector((s) => s.auth.me);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const accessToken = Cookies.get(STORAGE_KEY);
                if (accessToken) {
                    setSession(accessToken);
                    // If we already have user data in Redux (e.g. just logged in), skip /me
                    if (existingMe?.data) {
                        setIsUserLoggedIn(true);
                        return;
                    }
                    // After refresh: restore from localStorage so we don't depend on /me
                    const storedMe = getStoredMe();
                    if (storedMe?.data) {
                        dispatch(setCredentials({ token: accessToken, me: storedMe }));
                        setIsUserLoggedIn(true);
                        return;
                    }
                    const res = await axios.get(endpoints.auth.me);
                    const me = res.data?.data ?? res.data;
                    dispatch(setCredentials({ token: accessToken, me: { data: me } }));
                    setIsUserLoggedIn(true);
                } else {
                    dispatch(setCredentials({ token: null, me: null }));
                    navigate('/login');
                }
            } catch (err: unknown) {
                const status = (err as { response?: { status?: number } })?.response?.status;
                // Only clear auth on 401 (token invalid). Other errors: keep cookie and try stored me
                if (status === 401) {
                    dispatch(setCredentials({ token: null, me: null }));
                    navigate('/login');
                } else {
                    const storedMe = getStoredMe();
                    const accessToken = Cookies.get(STORAGE_KEY);
                    if (accessToken && storedMe?.data) {
                        dispatch(setCredentials({ token: accessToken, me: storedMe }));
                        setIsUserLoggedIn(true);
                    } else {
                        toast.error('فشل تحميل بيانات المستخدم');
                        dispatch(setCredentials({ token: null, me: null }));
                        navigate('/login');
                    }
                }
            }
        })();
    }, [dispatch, navigate, existingMe?.data]);

    if (!isUserLoggedIn) return <LoadingSpinner />;
    return <>{children}</>;
}
